import axios from 'axios';

import { HTTP_STATUS_BANNED } from './auth/authConstants';
import { getServerTimestampFromPayload } from './auth/authTokens';
import { updateClockDeltaFromServer } from './auth/clockDelta';
import {
  ensureFreshAccessTokenBestEffort,
  ensureJwtAuthBucketInBrowser,
  getAccessTokenFromBrowserCookie,
  isJwtAuthEnabledInBrowser,
  needsJwtHydrationFromSessionInBrowser,
  recoverFromUnauthorized,
} from './auth';

function getAuthorizationHeader(config) {
  if (config.headers) {
    if (config.headers.Authorization) {
      return config.headers.Authorization;
    }
    if (config.headers.common && config.headers.common.Authorization) {
      return config.headers.common.Authorization;
    }
  }
  if (
    axios.defaults.headers &&
    axios.defaults.headers.common &&
    axios.defaults.headers.common.Authorization
  ) {
    return axios.defaults.headers.common.Authorization;
  }
  return undefined;
}

function isTokenAuthHeader(authorization) {
  return typeof authorization === 'string' && /^Token\s+/i.test(authorization);
}

function shouldSkipJwtPipeline(config) {
  if (!isJwtAuthEnabledInBrowser()) {
    return true;
  }
  if (config.skipJwtAuth) {
    return true;
  }
  // Share / anonymous Token — skip JWT only when there is no access cookie.
  // If access exists, Bearer must win over defaults Token (service user).
  if (isTokenAuthHeader(getAuthorizationHeader(config))) {
    return !getAccessTokenFromBrowserCookie();
  }
  return false;
}

function setBearerHeader(config, token) {
  if (!token) {
    return config;
  }
  if (!config.headers) {
    config.headers = {};
  }
  config.headers.Authorization = 'Bearer ' + token;
  return config;
}

function applyServerTimestampFromResponse(res) {
  if (!res.ok) {
    return Promise.resolve(false);
  }
  return res
    .json()
    .catch(function () {
      return null;
    })
    .then(function (body) {
      const serverTimestamp = getServerTimestampFromPayload(body);
      if (serverTimestamp != null) {
        updateClockDeltaFromServer(serverTimestamp);
      }
      return true;
    });
}

/**
 * If access_token cookie is gone but httpOnly refresh_token (or session) remains,
 * restore the pair without redirecting to sign-in on failure (anonymous OK).
 */
export function hydrateJwtSessionOnBoot() {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  ensureJwtAuthBucketInBrowser();

  if (!isJwtAuthEnabledInBrowser()) {
    return Promise.resolve();
  }

  if (getAccessTokenFromBrowserCookie()) {
    return Promise.resolve();
  }

  return fetch('/api/refresh-tokens/', {
    method: 'POST',
    credentials: 'same-origin',
  })
    .then(function (refreshRes) {
      return applyServerTimestampFromResponse(refreshRes);
    })
    .then(function (ok) {
      if (ok) {
        return true;
      }
      if (!needsJwtHydrationFromSessionInBrowser()) {
        return false;
      }
      return fetch('/api/obtain-tokens/', {
        method: 'POST',
        credentials: 'same-origin',
      }).then(function (obtainRes) {
        return applyServerTimestampFromResponse(obtainRes);
      });
    })
    .catch(function () {
      return false;
    });
}

/**
 * Global axios JWT pipeline:
 * - proactive refresh / obtain before requests
 * - Bearer from access_token cookie
 * - single retry on 401/403 after recovery
 * - skips share/service Authorization Token when no JWT access cookie
 */
export function setupAxiosAuthInterceptors() {
  axios.interceptors.request.use(function (config) {
    ensureJwtAuthBucketInBrowser();

    if (shouldSkipJwtPipeline(config)) {
      return config;
    }

    return ensureFreshAccessTokenBestEffort().then(function () {
      return setBearerHeader(config, getAccessTokenFromBrowserCookie());
    });
  });

  axios.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      var config = error.config;
      var status = error.response && error.response.status;

      if (!config || shouldSkipJwtPipeline(config)) {
        return Promise.reject(error);
      }

      if (status === HTTP_STATUS_BANNED) {
        return Promise.reject(error);
      }

      if (status !== 401 && status !== 403) {
        return Promise.reject(error);
      }

      if (config._jwtRetry) {
        return Promise.reject(error);
      }

      return recoverFromUnauthorized().then(function (outcome) {
        if (outcome !== 'ok') {
          return Promise.reject(error);
        }

        config._jwtRetry = true;
        setBearerHeader(config, getAccessTokenFromBrowserCookie());
        return axios.request(config);
      });
    }
  );
}
